import { createContext, useContext, useRef } from 'react';

const AiCacheContext = createContext(null);

// TTLs mirror the backend (conservative — we just avoid refetching within a session).
export const AI_TTL = {
  PATIENT_SUMMARY:    4 * 60 * 60 * 1000,  // 4h
  RISK_TREND:         4 * 60 * 60 * 1000,  // 4h
  SYMPTOM_ANALYSIS:   6 * 60 * 60 * 1000,  // 6h
  POPULATION_INSIGHT: 30 * 60 * 1000,      // 30min
};

export function AiCacheProvider({ children }) {
  // Plain ref — no re-renders needed, just a session-scoped lookup table.
  const cache = useRef({});

  /**
   * Return cached data if fresh; otherwise call `fetcher()`, cache the result, and return it.
   * fetcher must return the resolved data value (not the axios response).
   */
  function getOrFetch(key, fetcher, ttlMs) {
    const entry = cache.current[key];
    if (entry && entry.expiresAt > Date.now()) {
      return Promise.resolve(entry.data);
    }
    return fetcher().then(data => {
      // Only cache truthy results — don't freeze a null/empty response for the TTL duration
      if (data != null && data !== false) {
        cache.current[key] = { data, expiresAt: Date.now() + ttlMs };
      }
      return data;
    });
  }

  /** Drop a single entry (e.g. after a new risk assessment is triggered). */
  function invalidate(key) {
    delete cache.current[key];
  }

  /** Drop all entries whose key starts with `prefix`. */
  function invalidatePrefix(prefix) {
    Object.keys(cache.current).forEach(k => {
      if (k.startsWith(prefix)) delete cache.current[k];
    });
  }

  return (
    <AiCacheContext.Provider value={{ getOrFetch, invalidate, invalidatePrefix }}>
      {children}
    </AiCacheContext.Provider>
  );
}

export function useAiCache() {
  return useContext(AiCacheContext);
}
